/** Client-side state store — fed by SSE, replaces TanStack Query for stats */
import { create } from "zustand";

export interface StreamStats {
    pending: number;
    downloading: number;
    stored: number;
    failed: number;
    expired: number;
    totalSize: number;
    totalDuration: number;
}

export interface ChunkFeedEvent {
    streamKey: string;
    sequence: number;
    size: number;
    ts: number;
}

export const RECENT_CHUNKS_MAX = 10;

interface DashboardStore {
    /** Per-stream stats keyed by streamKey (includes audio streams) */
    stats: Record<string, StreamStats>;
    /** Last N chunk events for the live feed panel (oldest first) */
    recentChunks: ChunkFeedEvent[];
    version: number;
    connected: boolean;

    hydrate: (streams: Array<{ streamKey: string; counts: StreamStats; totalSize: number; totalDuration: number }>, version: number) => void;
    /** Apply a coalesced batch of per-stream stats in a single render tick */
    updateStatsBatch: (entries: Array<[string, StreamStats]>, chunks: ChunkFeedEvent[], version: number) => void;
    setConnected: (v: boolean) => void;
}

export const useDashboardStore = create<DashboardStore>((set) => ({
    stats: {},
    recentChunks: [],
    version: 0,
    connected: false,

    hydrate: (streams, version) => set(() => {
        const stats: Record<string, StreamStats> = {};
        for (const s of streams) {
            stats[s.streamKey] = {
                pending: s.counts.pending,
                downloading: s.counts.downloading,
                stored: s.counts.stored,
                failed: s.counts.failed,
                expired: s.counts.expired,
                totalSize: s.totalSize,
                totalDuration: s.totalDuration,
            };
        }
        return { stats, version, connected: true };
    }),

    updateStatsBatch: (entries, chunks, version) => set((state) => {
        const stats = { ...state.stats };
        for (const [key, s] of entries) stats[key] = s;
        const recentChunks = chunks.length
            ? [...state.recentChunks, ...chunks].slice(-RECENT_CHUNKS_MAX)
            : state.recentChunks;
        return { stats, recentChunks, version };
    }),

    setConnected: (connected) => set({ connected }),
}));
